import React, { ComponentType, useCallback, useState } from "react"

import Input from "../../input"
import Dropdown from ".."
import { isDivider, Option, ValueOption } from "../selection-box/types"

type BlockedInputProps =
  | "onChange"
  | "onFocus"
  | "value"
  | "suffix"
  | "autoComplete"

type PermittedInputProps = Omit<
  React.ComponentProps<typeof Input>,
  BlockedInputProps
>
interface Props extends PermittedInputProps {
  className?: string
  options: readonly Option[]
  value?: string
  onSelectItem: (option: ValueOption | null) => void
  renderOption?: ComponentType<ValueOption>
  clearable?: boolean
  blurOnSelect?: boolean
}

const Autocomplete = React.forwardRef<HTMLDivElement, Props>(
  (
    { options, value, onSelectItem, className, renderOption, ...otherProps },
    ref,
  ) => {
    const [filterText, setFilterText] = useState<string | undefined>(undefined)

    const filteredOptions = filterText
      ? options.filter(
          o =>
            isDivider(o) ||
            o.label.toLowerCase().includes(filterText.toLowerCase()),
        )
      : options

    const handleSelectItem = useCallback(
      (option: ValueOption) => {
        setFilterText(undefined)
        onSelectItem(option)
      },
      [onSelectItem],
    )

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (value) {
          onSelectItem(null)
        }
        setFilterText(e.target.value)
      },
      [value, onSelectItem],
    )

    return (
      <Dropdown
        options={filteredOptions}
        renderOption={renderOption}
        onOptionSelect={handleSelectItem}
        value={value}
        className={className}
        onChange={handleChange}
        displayValue={filterText}
        ref={ref}
        clearable
        {...otherProps}
      />
    )
  },
)

Autocomplete.defaultProps = {
  className: "",
  renderOption: undefined,
}

Autocomplete.displayName = "Autocomplete"

export default Autocomplete
